import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, TablePagination, Alert, Avatar, ToggleButtonGroup, ToggleButton,
  Switch, FormControlLabel, Stack
} from '@mui/material';
import { Add, Search, Refresh, Edit, Delete, Person, LockReset, AdminPanelSettings, Group, VerifiedUser, GppBad } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { useAuthStore, ROLE_LABELS, ROLE_COLORS as RC } from '../../store/auth';

const MFA_REQUIRED_ROLES = new Set(['l2_engineer','super_admin','sub_super_admin','provider_admin','technical_admin','billing_admin','executive','reseller_admin']);

const PLATFORM_ROLES = [
  { value: 'super_admin',     label: 'Super Admin',     desc: 'Full platform access + system settings' },
  { value: 'sub_super_admin', label: 'Sub Super Admin',  desc: 'Full access, but all cost/financial figures hidden' },
  { value: 'technical_admin', label: 'Technical Admin', desc: 'SBC, routing, carriers, numbers, fraud, ACL' },
  { value: 'l2_engineer',     label: 'L2 Engineer',     desc: 'Monitoring only: live traffic, failures, SBC/services, logs. Read-only, no $ anywhere, 2FA required' },
  { value: 'billing_admin',   label: 'Billing Admin',   desc: 'Rates, invoices, payments, customers, import' },
  { value: 'executive',       label: 'Executive',       desc: 'Read-only: dashboard, analytics, CDRs, invoices' },
  { value: 'provider_admin',  label: 'Provider Admin',  desc: 'Full platform access (excl. system settings)' },
  { value: 'reseller_admin',  label: 'Reseller Admin',  desc: 'Reseller-level access' },
];
const CUSTOMER_ROLES = [
  { value: 'customer_admin', label: 'Customer Admin', desc: 'Customer portal management' },
  { value: 'customer_user',  label: 'Customer User',  desc: 'Customer portal view access' },
];

const PLATFORM_ROLE_VALUES = PLATFORM_ROLES.map(r => r.value);
const ROLE_COLORS: any = RC;

const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never';
const initials = (first: string, last: string, email: string) => {
  if (first || last) return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase();
  return (email?.[0] || '?').toUpperCase();
};

const emptyForm = { first_name: '', last_name: '', email: '', username: '', role: 'billing_admin', password: '', tenant_id: '', status: 'active', phone: '' };

export const UsersPage: React.FC = () => {
  const { user: me } = useAuthStore();
  const isSuperAdmin = me?.role === 'super_admin';
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [view, setView] = useState<'platform' | 'customer'>('platform');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [newPassword, setNewPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('limit', '50');
      params.set('offset', String(page * 50));
      const { data: res } = await api.get(`/users?${params}`);
      const rows: any[] = res.data || (Array.isArray(res) ? res : []);
      // Filter client-side: platform vs customer view
      const filtered = view === 'platform'
        ? rows.filter(r => PLATFORM_ROLE_VALUES.includes(r.role))
        : rows.filter(r => !PLATFORM_ROLE_VALUES.includes(r.role));
      setData(filtered);
      setTotal(filtered.length);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, page, view]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/tenants?limit=200').then(({ data: res }) => setTenants(res.data || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, role: view === 'platform' ? 'billing_admin' : 'customer_admin' });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({
      first_name: row.first_name || '',
      last_name:  row.last_name  || '',
      email:      row.email      || '',
      username:   row.username   || '',
      role:       row.role       || '',
      status:     row.status     || 'active',
      tenant_id:  row.tenant_id  || '',
      phone:      row.phone      || '',
      password:   '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload: any = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.tenant_id) delete payload.tenant_id;
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post('/users', payload);
      }
      setDialogOpen(false); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !editing) return;
    if (newPassword.length < 8) { setPwError('Password must be at least 8 characters'); return; }
    setPwError('');
    try {
      await api.put(`/users/${editing.id}`, { password: newPassword });
      setPwDialogOpen(false);
      setNewPassword('');
    } catch (e: any) { setPwError(e.response?.data?.message || 'Reset failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/users/${editing.id}`); setDeleteDialogOpen(false); load(); }
    catch (e) { console.error(e); }
  };

  const availableRoles = view === 'platform' ? PLATFORM_ROLES : CUSTOMER_ROLES;

  const handleResetMfa = async (user: any) => {
    if (!window.confirm(`Reset 2FA for ${user.email}? They will need to re-enrol on next login.`)) return;
    try {
      await api.patch(`/users/${user.id}/mfa/reset`);
      if (editing?.id === user.id) setEditing({ ...editing, mfa_enabled: false });
      enqueueSnackbar(`2FA reset for ${user.email}`, { variant: 'success' });
      load();
    } catch (e: any) {
      enqueueSnackbar(e.response?.data?.message || 'Reset failed', { variant: 'error' });
    }
  };

  // Super-admin: require (or un-require) 2FA for this user
  const handleSetMfaRequired = async (required: boolean) => {
    if (!editing) return;
    try {
      await api.patch(`/users/${editing.id}/mfa/require`, { required });
      setEditing({ ...editing, mfa_required: required });
      enqueueSnackbar(required
        ? `2FA now required for ${editing.email} — they'll set it up at next login`
        : `2FA requirement removed for ${editing.email}`, { variant: 'success' });
      load();
    } catch (e: any) {
      enqueueSnackbar(e.response?.data?.message || 'Update failed', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Person sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Users</Typography>
            <Typography variant="body2" color="text.secondary">{total} {view} users</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
            <ToggleButton value="platform"><AdminPanelSettings sx={{ mr: 0.5, fontSize: 16 }} />Platform</ToggleButton>
            <ToggleButton value="customer"><Group sx={{ mr: 0.5, fontSize: 16 }} />Customer</ToggleButton>
          </ToggleButtonGroup>
          <TextField size="small" placeholder="Search..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 200 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          {isSuperAdmin && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add User</Button>
          )}
        </Box>
      </Box>

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['User', 'Email / Username', 'Role', 'Tenant', 'Status', '2FA', 'Last Login', 'Created', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
                          {initials(row.first_name, row.last_name, row.email)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.email}</Typography>
                      {row.username && <Typography variant="caption" color="text.secondary">@{row.username}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={ROLE_LABELS[row.role] || row.role} size="small"
                        color={ROLE_COLORS[row.role] || 'default'} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                      {row.tenant_id
                        ? tenants.find(t => t.id === row.tenant_id)?.name || row.tenant_id.slice(0, 8)
                        : <Chip size="small" label="Platform" variant="outlined" />}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.status} size="small"
                        color={row.status === 'active' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{fmtDate(row.last_login)}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{fmtDate(row.created_at)}</TableCell>
                    <TableCell>
                      {MFA_REQUIRED_ROLES.has(row.role) ? (
                        row.mfa_enabled ? (
                          <Chip icon={<VerifiedUser />} label="Enabled" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip icon={<GppBad />} label="Not set" size="small" color="warning" variant="outlined" />
                        )
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit User">
                        <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton size="small" color="warning" onClick={() => { setEditing(row); setNewPassword(''); setPwError(''); setPwDialogOpen(true); }}>
                          <LockReset fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isSuperAdmin && MFA_REQUIRED_ROLES.has(row.role) && row.mfa_enabled && (
                        <Tooltip title="Reset 2FA (user must re-enrol)">
                          <IconButton size="small" color="secondary" onClick={() => handleResetMfa(row)}>
                            <GppBad fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isSuperAdmin && row.id !== me?.id && (
                        <Tooltip title="Delete User">
                          <IconButton size="small" color="error" onClick={() => { setEditing(row); setDeleteDialogOpen(true); }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No {view} users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={50}
              onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[50]} />
          </>
        )}
      </Card>

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit: ${[editing.first_name, editing.last_name].filter(Boolean).join(' ') || editing.email}` : 'New User'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="First Name" value={form.first_name}
                onChange={e => setForm({ ...form, first_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Last Name" value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Email *" type="email" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Username" value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                helperText="Leave blank to auto-generate from email" />
            </Grid>
            {!editing && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Password *" type="password" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  helperText="Minimum 8 characters" required />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Role *" value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}>
                {[...PLATFORM_ROLES, ...CUSTOMER_ROLES]
                  .filter(r => isSuperAdmin || r.value !== 'super_admin')
                  .map(r => (
                    <MenuItem key={r.value} value={r.value}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.desc}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            {isSuperAdmin && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Tenant" value={form.tenant_id}
                  onChange={e => setForm({ ...form, tenant_id: e.target.value })}>
                  <MenuItem value=""><em>Platform (no tenant)</em></MenuItem>
                  {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Phone" value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Status" value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>

            {/* ── Two-Factor Authentication (super-admin can require it) ── */}
            {editing && isSuperAdmin && (
              <Grid item xs={12}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, bgcolor: 'action.hover' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {editing.mfa_enabled ? <VerifiedUser color="success" fontSize="small" /> : <GppBad color={editing.mfa_required ? 'warning' : 'disabled'} fontSize="small" />}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Two-Factor Authentication</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {editing.mfa_enabled ? 'Enrolled — this account is protected by 2FA'
                            : editing.mfa_required ? 'Required — user must set it up at next login'
                            : 'Not enrolled'}
                        </Typography>
                      </Box>
                    </Box>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      labelPlacement="start"
                      label={<Typography variant="body2" fontWeight={600}>Require 2FA</Typography>}
                      control={
                        <Switch color="success"
                          checked={!!(editing.mfa_required || editing.mfa_enabled)}
                          disabled={!!editing.mfa_enabled}
                          onChange={e => handleSetMfaRequired(e.target.checked)} />
                      }
                    />
                  </Stack>
                  {editing.mfa_enabled && (
                    <Button size="small" color="warning" startIcon={<LockReset />} sx={{ mt: 1 }}
                      onClick={() => handleResetMfa(editing)}>
                      Reset 2FA (force re-enrolment)
                    </Button>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={saving || !form.email || (!editing && !form.password)}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save Changes' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Password Dialog ── */}
      <Dialog open={pwDialogOpen} onClose={() => setPwDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {[editing?.first_name, editing?.last_name].filter(Boolean).join(' ') || editing?.email}
          </Typography>
          {pwError && <Alert severity="error" sx={{ mb: 1 }}>{pwError}</Alert>}
          <TextField fullWidth size="small" label="New Password" type="password" value={newPassword}
            onChange={e => setNewPassword(e.target.value)} helperText="Minimum 8 characters" sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPwDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleResetPassword}
            disabled={newPassword.length < 8}>
            Set Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{[editing?.first_name, editing?.last_name].filter(Boolean).join(' ') || editing?.email}</strong>?
            This removes the account for good and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
